/**
 * Instagram API utility functions for frontend
 */

export interface InstagramPost {
  id: string;
  caption: string;
  mediaType: string;
  mediaUrl: string;
  permalink: string;
  timestamp: string;
  likeCount: number;
  commentsCount: number;
}

export interface InstagramSettings {
  connected: boolean;
  accountId: string | null;
  username: string | null;
  profilePicture: string | null;
  postsLimit: number;
  refreshInterval: number;
  columns: number;
  displayLimit?: number;
  lastRefresh: string | null;
}

export interface InstagramFeedResponse {
  posts: InstagramPost[];
  cached: boolean;
  connected: boolean;
  columns?: number;
  displayLimit?: number;
  warning?: string;
}

// Use relative URLs to leverage the proxy in package.json (http://127.0.0.1:10000)
// In production, this will be an empty string (same origin)
const API_BASE_URL = '';

/**
 * Get Instagram settings
 */
export const getInstagramSettings = async (): Promise<InstagramSettings> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/instagram/settings`);
    if (!response.ok) {
      throw new Error('Failed to fetch Instagram settings');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching Instagram settings:', error);
    throw error;
  }
};

/**
 * Save Instagram settings
 */
export const saveInstagramSettings = async (settings: {
  accessToken: string;
  accountId: string;
  postsLimit?: number;
  refreshInterval?: number;
  columns?: number;
}): Promise<{ success: boolean; settings?: any }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/instagram/settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save Instagram settings');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error saving Instagram settings:', error);
    throw error;
  }
};

/**
 * Save how many columns the public Instagram feed uses.
 */
export const saveInstagramColumns = async (
  columns?: number,
  displayLimit?: number
): Promise<{ success: boolean; columns: number; displayLimit?: number }> => {
  try {
    const body: { columns?: number; displayLimit?: number } = {};
    if (typeof columns === 'number') body.columns = columns;
    if (typeof displayLimit === 'number') body.displayLimit = displayLimit;
    const response = await fetch(`${API_BASE_URL}/api/instagram/columns`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to save Instagram layout');
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving Instagram columns:', error);
    throw error;
  }
};

/**
 * Get Instagram feed
 */
export const getInstagramFeed = async (): Promise<InstagramFeedResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/instagram/feed`, { cache: 'no-store' });
    if (!response.ok) {
      if (response.status === 404) {
        return { posts: [], cached: false, connected: false };
      }
      throw new Error('Failed to fetch Instagram feed');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching Instagram feed:', error);
    return { posts: [], cached: false, connected: false };
  }
};

/**
 * Refresh Instagram feed manually
 */
export const refreshInstagramFeed = async (): Promise<{ success: boolean; posts: InstagramPost[]; count: number }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/instagram/refresh`, {
      method: 'POST',
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to refresh Instagram feed');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error refreshing Instagram feed:', error);
    throw error;
  }
};

/**
 * Initiate Instagram OAuth connection
 */
export const connectInstagram = async (): Promise<{ authUrl: string; state: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/instagram/connect`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to initiate Instagram connection');
    }
    return await response.json();
  } catch (error) {
    console.error('Error initiating Instagram connection:', error);
    throw error;
  }
};

/**
 * Disconnect Instagram account
 */
export const disconnectInstagram = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/instagram/disconnect`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error('Failed to disconnect Instagram account');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error disconnecting Instagram:', error);
    throw error;
  }
};

